import { useEffect } from 'react';
import { useCommandCenter } from '../store';

const SPEEDS = [1, 2, 5, 10, 60, 300];

function formatHHMM(totalSec: number): string {
  const h = Math.floor(totalSec / 3600)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((totalSec % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${h}:${m}`;
}

export function TimeMachine() {
  const mode = useCommandCenter((s) => s.mode);
  const setMode = useCommandCenter((s) => s.setMode);
  const replay = useCommandCenter((s) => s.replay);
  const setReplay = useCommandCenter((s) => s.setReplay);
  const layers = useCommandCenter((s) => s.layers);
  const toggleLayer = useCommandCenter((s) => s.toggleLayer);

  // Auto-advance scrubber while playing.
  useEffect(() => {
    if (mode !== 'REPLAY' || !replay.playing) return;
    const id = setInterval(() => {
      setReplay({
        cursorSec: Math.min(86399, replay.cursorSec + replay.speed),
      });
    }, 1000);
    return () => clearInterval(id);
  }, [mode, replay.playing, replay.cursorSec, replay.speed, setReplay]);

  return (
    <footer className="flex h-[60px] items-center gap-3 border-t border-cc-border bg-cc-surface px-3 font-mono text-xs text-cc-fg">
      <div className="flex items-center border border-cc-border">
        <button
          onClick={() => setMode('LIVE')}
          className={`px-3 py-1 uppercase ${
            mode === 'LIVE' ? 'bg-cc-accent/20 text-cc-accent' : 'text-cc-muted hover:text-cc-fg'
          }`}
        >
          ● LIVE
        </button>
        <button
          onClick={() => setMode('REPLAY')}
          className={`px-3 py-1 uppercase ${
            mode === 'REPLAY' ? 'bg-cc-accent/20 text-cc-accent' : 'text-cc-muted hover:text-cc-fg'
          }`}
        >
          ▶ REPLAY
        </button>
      </div>

      {mode === 'REPLAY' ? (
        <>
          <input
            type="date"
            value={replay.date}
            onChange={(e) => setReplay({ date: e.target.value, cursorSec: 0, playing: false })}
            className="border border-cc-border bg-cc-bg px-2 py-1 text-cc-fg"
          />
          <div className="flex items-center gap-1">
            <button
              onClick={() => setReplay({ cursorSec: 0, playing: false })}
              className="border border-cc-border px-2 py-1 uppercase text-cc-muted hover:text-cc-accent"
            >
              ⏮
            </button>
            <button
              onClick={() => setReplay({ cursorSec: Math.max(0, replay.cursorSec - 60) })}
              className="border border-cc-border px-2 py-1 uppercase text-cc-muted hover:text-cc-accent"
            >
              ⏪
            </button>
            <button
              onClick={() => setReplay({ playing: !replay.playing })}
              className="border border-cc-border px-2 py-1 uppercase text-cc-accent"
            >
              {replay.playing ? '⏸' : '▶'}
            </button>
            <button
              onClick={() =>
                setReplay({
                  cursorSec: Math.min(86399, replay.cursorSec + 60),
                })
              }
              className="border border-cc-border px-2 py-1 uppercase text-cc-muted hover:text-cc-accent"
            >
              ⏩
            </button>
            <button
              onClick={() => setReplay({ cursorSec: 86399, playing: false })}
              className="border border-cc-border px-2 py-1 uppercase text-cc-muted hover:text-cc-accent"
            >
              ⏭
            </button>
          </div>
          <select
            value={replay.speed}
            onChange={(e) => setReplay({ speed: Number(e.target.value) })}
            className="border border-cc-border bg-cc-bg px-2 py-1 text-cc-fg"
          >
            {SPEEDS.map((s) => (
              <option key={s} value={s}>
                {s}×
              </option>
            ))}
          </select>
          <span className="tabular-nums text-cc-muted">{formatHHMM(replay.cursorSec)} / 23:59</span>
          <input
            type="range"
            min={0}
            max={86399}
            value={replay.cursorSec}
            onChange={(e) => setReplay({ cursorSec: Number(e.target.value), playing: false })}
            className="flex-1 accent-cc-accent"
          />
        </>
      ) : (
        <>
          <span className="inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-cc-ok" />
            LIVE STREAM
          </span>
          <span className="text-cc-muted">Updates fan-out via tenant.{'{id}'}.fleet.live</span>
        </>
      )}

      <div className="ml-auto flex items-center gap-1">
        <LayerToggle
          label="Geofences"
          active={layers.geofences}
          onClick={() => toggleLayer('geofences')}
        />
        <LayerToggle label="Heat" active={layers.heatmap} onClick={() => toggleLayer('heatmap')} />
        <LayerToggle
          label="Satellite"
          active={layers.satellite}
          onClick={() => toggleLayer('satellite')}
        />
      </div>
    </footer>
  );
}

function LayerToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`border px-2 py-1 uppercase ${
        active
          ? 'border-cc-accent bg-cc-accent/20 text-cc-accent'
          : 'border-cc-border bg-cc-bg text-cc-muted hover:text-cc-fg'
      }`}
    >
      {label}
    </button>
  );
}
